import React, { useEffect, useRef, useState } from "react";
import styles from "./styles.module.scss";
import EventLogSquare from "../../Components/EventLogSquare";
import { useViewport } from "../../Components/ViewportProvider";
import { mobileBreakpoint } from "../../Services/config";
import { format, isSameDay } from "date-fns";
import { EventsLog } from "State/slices/serviceUsersData-slice";
import { useAppSelector } from "State/hooks";
import { CombinedContinuousInsightType } from "@intelligentlilli/lilli-utils";

const regexMatchers = {
  M2: new RegExp("Bathroom/Wall/Motion$", "i"), // Bathroom/Wall/Motion
  M3: new RegExp("Bedroom/Wall/Motion$", "i"), //Bedroom/Wall/Motion,
  M1: new RegExp("Hall/Wall/Motion$", "i"), //Hall/Wall/Motion,
  D2: new RegExp("Kitchen/Fridge/Door$", "i"), // Kitchen/Fridge/Door
  D1: new RegExp("[\\w]+/Front/Door$|[\\w]+/Back/Door$|[\\w]+/Main/Door$", "i"), //<anything>/[Front|Back|Main]/Door
  D3: new RegExp("Hall/Secondary/Door$", "i"), // Hall/Secondary/Door
};

// Compute the bucket index for a given timestamp
const getBucketIndex = (time: string, isDesktopView: boolean): number => {
  const hours = parseInt(format(new Date(time), "HH"));
  const minutes = parseInt(format(new Date(time), "mm"));
  return isDesktopView ? hours * 4 + Math.floor(minutes / 15) : hours;
};

// Formats data for the event log: used on motion sensors (bathroom, bedroom, hallway)
const getParsedDataForBuckets = (
  motionEvents: string[] | undefined,
  numberOfBuckets: number,
  isDesktopView: boolean
): string[][] => {
  const parsedEvents: string[][] = Array.from(
    { length: numberOfBuckets },
    () => []
  );
  motionEvents?.forEach((time) => {
    parsedEvents[getBucketIndex(time, isDesktopView)].push(time);
  });
  return parsedEvents;
};

// Formats data for door sensors (front door, back door, fridge) that have openedAt/closedAt
const parseDoorEvents = (
  doorEvents: { openedAt?: string[]; closedAt?: string[] } | undefined,
  numberOfBuckets: number,
  isDesktopView: boolean
): { time: string; status: "opened" | "closed" }[][] => {
  const parsedEvents: { time: string; status: "opened" | "closed" }[][] =
    Array.from({ length: numberOfBuckets }, () => []);
  doorEvents?.openedAt?.forEach((time) => {
    parsedEvents[getBucketIndex(time, isDesktopView)].push({
      time,
      status: "opened",
    });
  });
  doorEvents?.closedAt?.forEach((time) => {
    parsedEvents[getBucketIndex(time, isDesktopView)].push({
      time,
      status: "closed",
    });
  });
  return parsedEvents;
};

type OutsideActivity = {
  insightId?: string;
  startAt?: string;
  endAt?: string;
  durationMinutes?: number;
  userState?: string;
  originalStart?: string;
  originalEnd?: string;
};

type DoorEvent = {
  time: string;
  status: "opened" | "closed";
};

type FrontDoorBucket = {
  events: DoorEvent[];
  outsideState: string;
  outsideData?: OutsideActivity;
};

const getAugmentedFrontDoorEvents = (
  frontDoorBuckets: DoorEvent[][],
  outsideActivities: OutsideActivity[] | undefined,
  buckets: { label: string; startTime: string; endTime: string }[],
  date: Date
): FrontDoorBucket[] => {
  const augmentedBuckets: FrontDoorBucket[] = frontDoorBuckets.map(
    (events, index) => {
      let outsideState = "";
      const outsideData = outsideActivities?.find((activity) => {
        const startDateIsSameDay = isSameDay(
          new Date(activity.originalStart || ""),
          date
        );
        const endDateIsSameDay = isSameDay(
          new Date(activity.originalEnd || ""),
          date
        );
        const activityStart = format(
          new Date(activity.originalStart || ""),
          "HH:mm"
        );
        const activityEnd = format(
          new Date(activity.originalEnd || ""),
          "HH:mm"
        );
        switch (true) {
          // Case 1 - outside the entire day
          case !startDateIsSameDay && !endDateIsSameDay:
            outsideState = "outside";
            return true;
          // Case 2 - outside overnight and still outside
          case !startDateIsSameDay && activityEnd > buckets[index].endTime:
            outsideState = "outside";
            return true;
          // Case 3 - outside and stays outside overnight
          case activityStart < buckets[index].startTime && !endDateIsSameDay:
            outsideState = "outside";
            return true;
          // Case 4 - returned home
          case activityEnd < buckets[index].endTime &&
            activityEnd >= buckets[index].startTime &&
            endDateIsSameDay:
            outsideState = "returns_home";
            return true;
          // Case 5 - goes outside
          case activityStart >= buckets[index].startTime &&
            activityStart < buckets[index].endTime &&
            startDateIsSameDay:
            outsideState = "goes_outside";
            return true;
          // Case 6 - is outside
          case activityStart < buckets[index].startTime &&
            activityEnd >= buckets[index].endTime:
            outsideState = "outside";
            return true;
          default:
            return undefined;
        }
      });

      return {
        events,
        outsideState,
        outsideData,
      };
    }
  );
  return augmentedBuckets;
};

const SensorNameLabel = ({ label }: { label: string }) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const element = labelRef.current;
    if (!element) return;

    setIsOverflowing(element.scrollWidth > element.clientWidth);
  }, [label]);

  return (
    <div
      className={`${styles.event_log_name} ${
        isOverflowing ? styles.event_log_name_overflowing : ""
      }`}
      data-full-label={isOverflowing ? label : undefined}
    >
      <span ref={labelRef} className={styles.event_log_name_text}>
        {label}
      </span>
    </div>
  );
};

const EventLog = ({
  reduxEventLogData,
  bathroomEvents,
  urlEndDateString,
  hubId,
  independenceData,
  date,
}: {
  reduxEventLogData: EventsLog | undefined;
  bathroomEvents: any[];
  urlEndDateString: string;
  hubId: string;
  independenceData: CombinedContinuousInsightType[];
}) => {
  const serviceUser = useAppSelector((state) =>
    state.serviceUsers.find((su) => su.id === parseInt(hubId))
  );

  const outsideDataDayView =
    (independenceData?.[0]?.activityData?.filter(
      (activity) => (activity as OutsideActivity).userState === "outside"
    ) as OutsideActivity[]) || [];

  const sensorLocations = serviceUser?.serviceUserProperties?.sensorLocations;

  const parsedSensorLocations: { [key: string]: string } = sensorLocations
    ? JSON.parse(sensorLocations)
    : {};
  const sensorLocationKeys = Object.keys(parsedSensorLocations);

  const getSensorLabel = (sensorKey: keyof typeof regexMatchers) => {
    const matchedLocation = sensorLocationKeys.find((location) =>
      regexMatchers[sensorKey].test(location)
    );

    const label = matchedLocation
      ? parsedSensorLocations[matchedLocation]
      : "Unknown";
    const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
    return capitalized;
  };

  const frontDoorLabel = getSensorLabel("D1");
  const backDoorLabel = getSensorLabel("D3");
  const motionSensorLabel = getSensorLabel("M1");
  const fridgeSensorLabel = getSensorLabel("D2");
  const bathroomSensorLabel = getSensorLabel("M2");
  const bedroomSensorLabel = getSensorLabel("M3");

  // Not all days will have back door data, so check if any of the days in the event log have back door data to decide whether to render the back door row
  const hasBackdoor = !!Object.values(reduxEventLogData || {})?.find(
    (events) => events["hall.secondary.door"]
  );

  const daysData = reduxEventLogData?.[urlEndDateString];
  // Extract the different streams
  const frontDoorEvents = daysData?.["hall.main.door"];
  const movementEvents = daysData?.["hall.wall.motion"];
  const bedroomEvents = daysData?.["bedroom.wall.motion"];
  const fridgeEvents = daysData?.["kitchen.fridge.door"];
  const backDoorEvents = daysData?.["hall.secondary.door"];

  // Get the width of the users screen for responsive js
  const { width } = useViewport();
  const desktopView = width > mobileBreakpoint;
  // The number of buckets (squares) the event log renders depends on screen size. 15min buckets for mobile (24hrs * 4 = 96) and hourly buckets otherwise
  const numBuckets = desktopView ? 96 : 24;
  const [closeTooltip, setCloseTooltip] = useState(false);

  // Create a list of the time buckets on mount
  // 1- Generate hourly time buckets
  const hours = [];
  for (let i = 0; i < 24; i++) {
    hours.push(i);
  }
  const buckets: { label: string; startTime: string; endTime: string }[] = [];
  // 2- Label the buckets
  hours.forEach((hour) => {
    // Add a "0" for hours less than 10 so the time is in the 03:XX format.
    const formattedHour = `${hour < 10 ? "0" : ""}${hour}`;
    // For larger screens each bucket is 15 mins
    const minutes = ["00", "15", "30", "45"];
    if (desktopView) {
      minutes.forEach((minute, index) => {
        buckets.push({
          label: `${formattedHour}:${minute}`,
          startTime: `${formattedHour}:${minute}`,
          endTime:
            index !== 3
              ? `${formattedHour}:${minutes[index + 1]}`
              : `${hour + 1 < 10 ? "0" : ""}${hour + 1}:00`,
        });
      });
    }
    // For smaller screens 1 hour buckets are used
    else {
      buckets.push({
        label: `${formattedHour}:00`,
        startTime: `${formattedHour}:00`,
        endTime: `${hour + 1 < 10 ? "0" : ""}${hour + 1}:00`,
      });
    }
  });

  const parsedFrontDoorEvents = parseDoorEvents(
    frontDoorEvents,
    numBuckets,
    desktopView
  );
  const parsedFrontDoorEventsWithOutside = getAugmentedFrontDoorEvents(
    parsedFrontDoorEvents,
    outsideDataDayView,
    buckets,
    date
  );

  const parsedBackDoorEvents = parseDoorEvents(
    backDoorEvents,
    numBuckets,
    desktopView
  );

  const parsedMovementEvents = getParsedDataForBuckets(
    movementEvents,
    numBuckets,
    desktopView
  );

  const parsedFridgeEvents = parseDoorEvents(
    fridgeEvents,
    numBuckets,
    desktopView
  );

  const parsedBathroomEvents = getParsedDataForBuckets(
    bathroomEvents,
    numBuckets,
    desktopView
  );

  const parsedBedroomEvents = getParsedDataForBuckets(
    bedroomEvents,
    numBuckets,
    desktopView
  );

  const [activeOutsideHighlightKey, setActiveOutsideHighlightKey] = useState<
    string | null
  >(null);

  let sensorData = [
    {
      sticker: "D1",
      label: "Main door sensor",
      location: frontDoorLabel,
      img: "/images/installation/sensors/door/door_thumb.png",
      alt: "door icon",
    },
    {
      sticker: "M1",
      label: "Motion sensor",
      location: motionSensorLabel,
      img: "/images/installation/sensors/motion/motion_thumb.png",
      alt: "motion icon",
    },
    {
      sticker: "D2",
      label: "Fridge sensor",
      location: fridgeSensorLabel,
      img: "/images/installation/sensors/door/door_thumb.png",
      alt: "door icon",
    },
    {
      sticker: "M2",
      label: "Bathroom sensor",
      location: bathroomSensorLabel,
      img: "/images/installation/sensors/motion/motion_thumb.png",
      alt: "motion icon",
    },
    {
      sticker: "M3",
      label: "Bedroom sensor",
      location: bedroomSensorLabel,
      img: "/images/installation/sensors/motion/motion_thumb.png",
      alt: "motion icon",
    },
  ];
  if (hasBackdoor) {
    sensorData = sensorData.toSpliced(1, 0, {
      sticker: "D3",
      label: "Secondary door sensor",
      location: backDoorLabel,
      img: "/images/installation/sensors/door/door_thumb.png",
      alt: "door icon",
    });
  }

  return (
    <div className={styles.event_log_container}>
      <h2>Event log</h2>
      <p>Tap or hover over each time block to see exact times of events</p>
      <div
        className={styles.event_log_log}
        onScroll={() => setCloseTooltip(true)}
      >
        <div className={styles.event_log_labels}>
          <div className={styles.event_log_name}></div>
          <SensorNameLabel
            label={frontDoorLabel === "Unknown" ? "Main door" : frontDoorLabel}
          />
          {hasBackdoor && (
            <SensorNameLabel
              label={
                backDoorLabel === "Unknown" ? "Secondary door" : backDoorLabel
              }
            />
          )}
          <SensorNameLabel
            label={
              motionSensorLabel === "Unknown" ? "Motion" : motionSensorLabel
            }
          />
          <SensorNameLabel
            label={
              fridgeSensorLabel === "Unknown" ? "Fridge" : fridgeSensorLabel
            }
          />
          <SensorNameLabel label="Bathroom" />
          <SensorNameLabel label="Bedroom" />
        </div>

        <div className={styles.event_log_rows_container}>
          <div className={styles.events}>
            {buckets?.map((eventInterval, index) => (
              <div className={styles.label} key={index}>
                {index % (desktopView ? 4 : 2) === 0 && eventInterval.label}
              </div>
            ))}
          </div>

          <div className={styles.events}>
            {parsedFrontDoorEventsWithOutside?.map((bucket, index) => (
              <EventLogSquare
                key={index}
                value={bucket.events.length}
                events={bucket.events}
                outsideState={bucket.outsideState}
                outsideData={bucket.outsideData}
                outsideHighlightKey={bucket.outsideData?.insightId || ""}
                activeOutsideHighlightKey={activeOutsideHighlightKey}
                setActiveOutsideHighlightKey={setActiveOutsideHighlightKey}
                index={index}
                entranceDoor
                time={buckets[index]}
                closeTooltip={closeTooltip}
                setCloseTooltip={setCloseTooltip}
              />
            ))}
          </div>
          {hasBackdoor && (
            <div className={styles.events}>
              {parsedBackDoorEvents?.map((bucket, index) => (
                <EventLogSquare
                  key={index}
                  value={bucket.length}
                  events={bucket}
                  index={index}
                  entranceDoor
                  time={buckets[index]}
                  closeTooltip={closeTooltip}
                  setCloseTooltip={setCloseTooltip}
                />
              ))}
            </div>
          )}
          <div className={styles.events}>
            {parsedMovementEvents?.map((bucket, index) => (
              <EventLogSquare
                key={index}
                value={bucket.length}
                events={bucket}
                index={index}
                time={buckets[index]}
                closeTooltip={closeTooltip}
                setCloseTooltip={setCloseTooltip}
              />
            ))}
          </div>
          <div className={styles.events}>
            {parsedFridgeEvents?.map((bucket, index) => (
              <EventLogSquare
                key={index}
                value={bucket.length}
                events={bucket}
                index={index}
                entranceDoor
                time={buckets[index]}
                closeTooltip={closeTooltip}
                setCloseTooltip={setCloseTooltip}
              />
            ))}
          </div>
          <div className={styles.events}>
            {parsedBathroomEvents?.map((bucket, index) => (
              <EventLogSquare
                key={index}
                value={bucket.length}
                events={bucket}
                index={index}
                time={buckets[index]}
                closeTooltip={closeTooltip}
                setCloseTooltip={setCloseTooltip}
              />
            ))}
          </div>
          <div className={styles.events}>
            {parsedBedroomEvents?.map((bucket, index) => (
              <EventLogSquare
                key={index}
                value={bucket.length}
                events={bucket}
                index={index}
                time={buckets[index]}
                closeTooltip={closeTooltip}
                setCloseTooltip={setCloseTooltip}
              />
            ))}
          </div>
        </div>
      </div>
      <h2>Sensor locations</h2>
      <div className={styles.sensors}>
        {sensorData.map((sensor, index) => (
          <div className={styles.sensor} key={index}>
            <img
              src={sensor.img}
              className={styles.sensor_image}
              alt={sensor.alt}
            />
            <div className={styles.sensor_info}>
              <span>{sensor.label}</span>
              <span className={styles.sensor_label_bold}>
                {sensor.location}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EventLog;
